import React, { useEffect, useState } from 'react';
import { Database, Upload } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { api } from '../lib/api';
import { usePilotContext } from '../hooks/usePilotContext';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const SAMPLE = `{
  "scan_id": "scan-pilot-001",
  "customer_id": "default",
  "scanned_at": "2026-04-02T12:00:00Z",
  "devices": [
    {
      "ip": "10.0.0.50",
      "hostname": "plc-line-1",
      "device_type": "PLC",
      "open_ports": [502, 102],
      "protocols": ["modbus", "s7comm"],
      "raw_metadata": { "zone": "ot" }
    }
  ]
}`;

export default function AssetsPage() {
  const { context } = usePilotContext();
  const [jsonText, setJsonText] = useState(SAMPLE);
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchAssets = async () => {
    if (!context.customer_id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100', offset: '0' });
      if (q.trim()) params.set('q', q.trim());
      const data = await api.get(`/api/assets/${context.customer_id}?${params}`);
      setAssets(data.assets || []);
      setTotal(data.total ?? 0);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, [context.customer_id]);

  const ingest = async () => {
    if (!context.customer_id) return;
    let body;
    try {
      body = JSON.parse(jsonText);
    } catch {
      toast.error('Invalid JSON');
      return;
    }
    try {
      const res = await api.post(`/api/discovery/${context.customer_id}/ingest`, body);
      toast.success(`Ingested ${res.devices_upserted ?? 0} devices (scan ${res.scan_id})`);
      await fetchAssets();
    } catch (e) {
      toast.error(e.message || 'Ingest failed');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground">Assets &amp; discovery</h1>
        <p className="text-muted-foreground">
          CMDB inventory (PostgreSQL) updated by discovery ingest. Then run a{' '}
          <Link className="text-primary underline-offset-4 hover:underline" to="/risk">
            risk scan
          </Link>{' '}
          on the latest scan file.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Ingest discovery JSON
          </CardTitle>
          <CardDescription>
            POSTs to <code className="text-xs">/api/discovery/&#123;customer_id&#125;/ingest</code> — same schema as file-based
            scans under <code className="text-xs">discovery_results/</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <textarea
            className="min-h-[200px] w-full rounded-md border border-border bg-secondary/30 p-3 font-mono text-xs text-foreground"
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={ingest}>
              Ingest &amp; upsert CMDB
            </Button>
            <Button type="button" variant="outline" onClick={() => setJsonText(SAMPLE)}>
              Reset sample
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-primary" />
                Asset inventory
              </CardTitle>
              <CardDescription>
                {total} record(s) for tenant <span className="font-mono text-foreground">{context.customer_id}</span>
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Filter IP / hostname / type"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-64"
              />
              <Button variant="secondary" size="sm" onClick={fetchAssets} disabled={loading}>
                Search
              </Button>
              <Button variant="outline" size="sm" onClick={fetchAssets} disabled={loading}>
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && assets.length === 0 ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : assets.length === 0 ? (
            <p className="text-sm text-muted-foreground">No CMDB rows yet — ingest a discovery payload above.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-secondary/50 text-muted-foreground">
                  <tr>
                    <th className="p-2 font-medium">IP</th>
                    <th className="p-2 font-medium">Hostname</th>
                    <th className="p-2 font-medium">Type</th>
                    <th className="p-2 font-medium">Firmware</th>
                    <th className="p-2 font-medium">Role</th>
                    <th className="p-2 font-medium">Criticality</th>
                    <th className="p-2 font-medium">Scan</th>
                    <th className="p-2 font-medium">Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {assets.map((a) => (
                    <tr key={`${a.ip}-${a.scan_id}`} className="border-t border-border">
                      <td className="p-2 font-mono">{a.ip}</td>
                      <td className="p-2">{a.hostname || '—'}</td>
                      <td className="p-2">{a.device_type || '—'}</td>
                      <td className="p-2 text-xs">{a.firmware_version || '—'}</td>
                      <td className="p-2 text-xs">{a.asset_role || '—'}</td>
                      <td className="p-2 text-xs">{a.criticality || '—'}</td>
                      <td className="p-2 font-mono text-xs">{a.scan_id || '—'}</td>
                      <td className="p-2 text-xs text-muted-foreground">{a.last_seen_at || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
